/**
 * Social Wellness Coach - Recommendation Engine
 * Analyzes user's social needs and friendship activity to recommend
 * the single most impactful next action to reduce loneliness.
 */

/**
 * Calculate Social Fulfillment Score (SFS)
 * Compares current interaction frequency against desired frequency
 */
const calculateSocialFulfillmentScore = (socialProfile, friendshipTracker) => {
  const desiredWeekly = socialProfile.desired_weekly_interactions || 0;
  
  // Count interactions in the last 7 days
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  let recentInteractionCount = 0;
  friendshipTracker.forEach(contact => {
    if (contact.recent_interactions) {
      contact.recent_interactions.forEach(interaction => {
        const interactionDate = new Date(interaction.date);
        if (interactionDate >= sevenDaysAgo) {
          recentInteractionCount++;
        }
      });
    }
  });
  
  const fulfillmentRatio = desiredWeekly > 0 ? recentInteractionCount / desiredWeekly : 0;
  
  if (fulfillmentRatio >= 0.8) return 'HIGH';
  if (fulfillmentRatio >= 0.5) return 'MEDIUM';
  return 'LOW';
};

/**
 * Assess relationship health for each contact
 * Returns: 'fading' 🔴, 'dormant' 🟡, or 'active' 🟢
 */
const assessRelationshipHealth = (contact) => {
  if (!contact.last_contact_date) return 'dormant';
  
  const lastContact = new Date(contact.last_contact_date);
  const now = new Date();
  const daysSinceContact = Math.floor((now - lastContact) / (1000 * 60 * 60 * 24));
  const usualGap = contact.usual_contact_gap_days || 14;
  
  // Active: contacted within usual gap
  if (daysSinceContact <= usualGap) {
    return 'active';
  }
  
  // Fading: past usual gap but not too long (within 2x gap)
  if (daysSinceContact <= usualGap * 2) {
    return 'fading';
  }
  
  // Dormant: way past usual gap
  return 'dormant';
};

/**
 * Map energy level to action tier
 */
const getActionTier = (energyLevel) => {
  const tiers = {
    'low': {
      actions: ['voice_note', 'reaction', 'meme', 'short_text'],
      effort: '🟢 Easy'
    },
    'medium': {
      actions: ['check_in_call', 'check_in_message'],
      effort: '🟡 Medium'
    },
    'high': {
      actions: ['schedule_hangout', 'attend_event', 'new_introduction'],
      effort: '🔴 Big step'
    }
  };
  
  return tiers[energyLevel] || tiers['medium'];
};

/**
 * Generate recommendation based on friendship goal
 */
const generateRecommendation = (socialProfile, friendshipTracker) => {
  const { friendship_goal, current_energy_today, loneliness_score } = socialProfile;
  
  // Assess all contacts
  const contactsWithHealth = friendshipTracker.map(contact => ({
    ...contact,
    health: assessRelationshipHealth(contact)
  }));
  
  // Get action tier based on energy
  const actionTier = getActionTier(current_energy_today);
  
  let recommendedContact = null;
  let actionType = null;
  let reason = '';
  let howTo = '';
  
  // Strategy based on friendship goal
  if (friendship_goal === 'deepen_existing') {
    // Prioritize Active or Fading close friends
    const closeFriends = contactsWithHealth.filter(c => 
      ['close', 'lifelong', 'chosen-family'].includes(c.relationship_type) || c.countsAsCloseFriend === true
    );
    
    // First try fading close friends with high quality
    const fadingClose = closeFriends
      .filter(c => c.health === 'fading')
      .sort((a, b) => (b.avg_interaction_quality || 0) - (a.avg_interaction_quality || 0));
    
    if (fadingClose.length > 0) {
      recommendedContact = fadingClose[0];
      reason = `You haven't connected with ${recommendedContact.name} in a while, but your past interactions were meaningful. Reaching out now could rekindle that closeness.`;
      actionType = current_energy_today === 'low' ? 'short_text' : 'check_in_call';
    } else {
      // Fall back to active close friends
      const activeClose = closeFriends.filter(c => c.health === 'active');
      if (activeClose.length > 0) {
        recommendedContact = activeClose[0];
        reason = `Your connection with ${recommendedContact.name} is strong. A deeper conversation could strengthen your bond even more.`;
        actionType = current_energy_today === 'high' ? 'schedule_hangout' : 'check_in_call';
      }
    }
  } else if (friendship_goal === 'expand_network') {
    // Prioritize Dormant contacts or suggest new outreach
    const dormant = contactsWithHealth
      .filter(c => c.health === 'dormant')
      .sort((a, b) => (b.avg_interaction_quality || 0) - (a.avg_interaction_quality || 0));
    
    if (dormant.length > 0) {
      recommendedContact = dormant[0];
      reason = `${recommendedContact.name} is someone you've lost touch with. Reconnecting could open up new opportunities for friendship.`;
      actionType = current_energy_today === 'low' ? 'short_text' : 'check_in_message';
    } else {
      // Suggest new outreach
      return {
        action: "Reach out to someone new or join a social activity",
        reason: "Expanding your network means making new connections. Consider joining a local group, event, or reaching out to someone you've been meaning to meet.",
        howTo: "Check out local meetups, community events, or send a message to someone you've been curious about.",
        effort: actionTier.effort,
        isNewConnection: true
      };
    }
  } else if (friendship_goal === 'reconnect') {
    // Prioritize Fading contacts with highest past quality
    const fading = contactsWithHealth
      .filter(c => c.health === 'fading')
      .sort((a, b) => (b.avg_interaction_quality || 0) - (a.avg_interaction_quality || 0));
    
    if (fading.length > 0) {
      recommendedContact = fading[0];
      reason = `You and ${recommendedContact.name} used to have great conversations. It's been a while, but the foundation is still there.`;
      actionType = current_energy_today === 'low' ? 'short_text' : 'check_in_call';
    }
  }
  
  // If no specific contact found, pick the most urgent fading contact
  if (!recommendedContact) {
    const fading = contactsWithHealth
      .filter(c => c.health === 'fading')
      .sort((a, b) => {
        const aDays = Math.floor((new Date() - new Date(a.last_contact_date)) / (1000 * 60 * 60 * 24));
        const bDays = Math.floor((new Date() - new Date(b.last_contact_date)) / (1000 * 60 * 60 * 24));
        return bDays - aDays; // Most urgent first
      });
    
    if (fading.length > 0) {
      recommendedContact = fading[0];
      reason = `It's been a while since you connected with ${recommendedContact.name}. A quick check-in could mean a lot.`;
      actionType = current_energy_today === 'low' ? 'short_text' : 'check_in_message';
    } else {
      // Check for dormant contacts
      const dormant = contactsWithHealth
        .filter(c => c.health === 'dormant')
        .sort((a, b) => (b.avg_interaction_quality || 0) - (a.avg_interaction_quality || 0));
      
      if (dormant.length > 0) {
        recommendedContact = dormant[0];
        reason = `You haven't connected with ${recommendedContact.name} in a long time. Reaching out could rekindle your friendship.`;
        actionType = current_energy_today === 'low' ? 'short_text' : 'check_in_message';
      } else {
        // All contacts are active - suggest deepening or adding more
        const active = contactsWithHealth.filter(c => c.health === 'active');
        if (active.length > 0) {
          // Pick the one with lowest reciprocity (they don't initiate much)
          const lowReciprocity = active
            .filter(c => (c.reciprocity_score || 0.5) < 0.3)
            .sort((a, b) => (a.reciprocity_score || 0.5) - (b.reciprocity_score || 0.5));
          
          if (lowReciprocity.length > 0) {
            recommendedContact = lowReciprocity[0];
            reason = `You usually initiate contact with ${recommendedContact.name}. A thoughtful check-in could help balance the relationship.`;
            actionType = current_energy_today === 'high' ? 'schedule_hangout' : 'check_in_call';
          } else {
            // All active and good reciprocity - suggest deepening
            recommendedContact = active[0];
            reason = `Your connection with ${recommendedContact.name} is going well. A deeper conversation could strengthen your bond.`;
            actionType = current_energy_today === 'high' ? 'schedule_hangout' : 'check_in_call';
          }
        }
      }
    }
  }
  
  // Generate action text and how-to
  if (recommendedContact) {
    const actionTexts = {
      'voice_note': `Send ${recommendedContact.name} a voice note`,
      'reaction': `React to ${recommendedContact.name}'s recent post or story`,
      'meme': `Send ${recommendedContact.name} a funny meme or photo`,
      'short_text': `Send ${recommendedContact.name} a quick text`,
      'check_in_message': `Send ${recommendedContact.name} a thoughtful message`,
      'check_in_call': `Call ${recommendedContact.name} for a catch-up`,
      'schedule_hangout': `Plan something with ${recommendedContact.name}`,
      'attend_event': `Invite ${recommendedContact.name} to an event`,
      'new_introduction': `Introduce yourself to someone new`
    };
    
    const howToTexts = {
      'voice_note': `Record a quick voice message saying you were thinking of them. Keep it light and warm.`,
      'reaction': `Double-tap their post or send a quick reaction emoji with a short comment.`,
      'meme': `Share something that reminded you of them or made you laugh.`,
      'short_text': `"Hey! Been thinking about you. How are things?"`,
      'check_in_message': `"Hey ${recommendedContact.name}! It's been a while. How have you been? Would love to catch up."`,
      'check_in_call': `Give them a call when you have 15-20 minutes. Start with "I was just thinking about you!"`,
      'schedule_hangout': `"Want to grab coffee this week? I'd love to catch up properly."`,
      'attend_event': `"There's [event] happening this weekend - want to go together?"`,
      'new_introduction': `"Hi! I've been meaning to reach out. I'd love to get to know you better."`
    };
    
    return {
      action: actionTexts[actionType] || `Reach out to ${recommendedContact.name}`,
      reason: reason || `Connecting with ${recommendedContact.name} aligns with your friendship goals.`,
      howTo: howToTexts[actionType] || `Send them a message or give them a call.`,
      effort: actionTier.effort,
      contactName: recommendedContact.name,
      isNewConnection: false
    };
  }
  
  // Fallback if no contacts
  return {
    action: "Add your first friend to start tracking connections",
    reason: "Building meaningful friendships starts with taking the first step.",
    howTo: "Go to the Friendship Tracker and add someone you'd like to stay connected with.",
    effort: '🟡 Medium',
    isNewConnection: false
  };
};

/**
 * Main function to generate recommendation
 */
export const getFriendshipRecommendation = (socialProfile, friendshipTracker, friendshipNeeds = null) => {
  if (!socialProfile) {
    return null;
  }
  
  // If no friends at all, suggest adding first friend
  if (!friendshipTracker || friendshipTracker.length === 0) {
    return {
      action: "Add your first friend to start tracking connections",
      reason: "Building meaningful friendships starts with taking the first step.",
      howTo: "Go to the Friendship Tracker and add someone you'd like to stay connected with.",
      effort: '🟡 Medium',
      isNewConnection: false
    };
  }
  
  // Calculate SFS (for internal use, not shown to user)
  const sfs = calculateSocialFulfillmentScore(socialProfile, friendshipTracker);
  
  // Check progress toward close friends goal
  const closeFriendsGoal = friendshipNeeds?.preferences?.closeFriendCount;
  const closeFriendTypes = ['close', 'lifelong', 'chosen-family'];
  // Count friends as "close" if:
  // 1. Their relationship type is in the close friend types, OR
  // 2. They have a countsAsCloseFriend flag set to true
  const currentCloseFriends = friendshipTracker.filter(c => 
    closeFriendTypes.includes(c.relationship_type) || c.countsAsCloseFriend === true
  ).length;
  const totalFriends = friendshipTracker.length;
  
  console.log('Close friends goal check:', {
    currentCloseFriends,
    target: closeFriendsGoal?.target,
    totalFriends,
    friendshipTracker: friendshipTracker.map(c => ({ name: c.name, type: c.relationship_type }))
  });
  
  if (closeFriendsGoal && closeFriendsGoal.target) {
    const targetCloseFriends = closeFriendsGoal.target;
    const remaining = targetCloseFriends - currentCloseFriends;
    
    // If user is below their goal, consider suggesting to add more
    if (remaining > 0 && currentCloseFriends < targetCloseFriends) {
      // Check if there are existing friends that need attention
      const contactsWithHealth = friendshipTracker.map(contact => ({
        ...contact,
        health: assessRelationshipHealth(contact)
      }));
      
      const fading = contactsWithHealth.filter(c => c.health === 'fading');
      const dormant = contactsWithHealth.filter(c => c.health === 'dormant');
      
      // If user has friends but they're not close friends, suggest deepening existing relationships first
      if (totalFriends > 0 && currentCloseFriends === 0 && totalFriends < targetCloseFriends) {
        // User has friends but none are close - suggest deepening
        const nonCloseFriends = contactsWithHealth.filter(c => 
          !closeFriendTypes.includes(c.relationship_type) && c.countsAsCloseFriend !== true
        );
        if (nonCloseFriends.length > 0) {
          // Prioritize fading/dormant non-close friends for deepening
          const candidate = (fading.length > 0 ? fading : dormant.length > 0 ? dormant : nonCloseFriends)[0];
          if (candidate) {
            const actionTier = getActionTier(socialProfile.current_energy_today);
            return {
              action: `Deepen your connection with ${candidate.name} to build a closer friendship`,
              reason: `You have ${totalFriends} friend${totalFriends !== 1 ? 's' : ''} but want ${targetCloseFriends} close friends. Deepening your relationship with ${candidate.name} could help you reach your goal.`,
              howTo: `Reach out to ${candidate.name} for a meaningful conversation. Share something personal or ask about their life beyond surface-level topics.`,
              effort: actionTier.effort,
              isNewConnection: false
            };
          }
        }
      }
      
      // If there are fading/dormant friends, prioritize reconnecting (but mention goal context)
      if (fading.length > 0 || dormant.length > 0) {
        // Continue with normal recommendation logic, but we'll enhance the reason
      } else if (totalFriends >= targetCloseFriends && currentCloseFriends < targetCloseFriends) {
        // User has enough friends but not enough close ones - suggest deepening
        const nonCloseFriends = contactsWithHealth.filter(c => 
          !closeFriendTypes.includes(c.relationship_type) && c.countsAsCloseFriend !== true
        );
        if (nonCloseFriends.length > 0) {
          const candidate = nonCloseFriends[0];
          const actionTier = getActionTier(socialProfile.current_energy_today);
          return {
            action: `Deepen your connection with ${candidate.name}`,
            reason: `You have ${totalFriends} friend${totalFriends !== 1 ? 's' : ''} but only ${currentCloseFriends} close friend${currentCloseFriends !== 1 ? 's' : ''}. You want ${targetCloseFriends} close friends. Deepening your relationship with ${candidate.name} could help you reach your goal.`,
            howTo: `Plan a deeper conversation with ${candidate.name}. Ask about their goals, challenges, or share something meaningful about your own life.`,
            effort: actionTier.effort,
            isNewConnection: false
          };
        }
      } else if (totalFriends < targetCloseFriends) {
        // User needs more friends overall
        const actionTier = getActionTier(socialProfile.current_energy_today);
        return {
          action: `You have ${currentCloseFriends} close friend${currentCloseFriends !== 1 ? 's' : ''} and ${totalFriends} total friend${totalFriends !== 1 ? 's' : ''}. Add ${remaining} more close friend${remaining !== 1 ? 's' : ''} to reach your goal of ${targetCloseFriends}.`,
          reason: `You're working toward ${targetCloseFriends} close friends and currently have ${currentCloseFriends}. Adding ${remaining} more will help you reach your goal.`,
          howTo: "Think about someone you'd like to deepen a connection with, or someone you've been meaning to reach out to. Add them to your tracker and start building that relationship.",
          effort: actionTier.effort,
          isNewConnection: true
        };
      }
    }
  }
  
  // Generate recommendation based on existing friends
  const recommendation = generateRecommendation(socialProfile, friendshipTracker);
  
  // Enhance recommendation with goal context if applicable
  if (closeFriendsGoal && closeFriendsGoal.target && recommendation) {
    const closeFriendTypes = ['close', 'lifelong', 'chosen-family'];
    const currentCloseFriends = friendshipTracker.filter(c => 
      closeFriendTypes.includes(c.relationship_type) || c.countsAsCloseFriend === true
    ).length;
    const targetCloseFriends = closeFriendsGoal.target;
    const remaining = targetCloseFriends - currentCloseFriends;
    
    if (remaining > 0 && currentCloseFriends < targetCloseFriends && !recommendation.isNewConnection) {
      // Add context about goal progress to the reason
      recommendation.reason = `${recommendation.reason} (You're working toward ${targetCloseFriends} close friends and currently have ${currentCloseFriends}.)`;
    }
  }
  
  // Add loneliness acknowledgment if needed
  if (socialProfile.loneliness_score >= 7) {
    recommendation.acknowledgment = "I know it can feel lonely sometimes. You're not alone in this, and small steps forward matter.";
  }
  
  return recommendation;
};

/**
 * Map existing app data to expected input format
 */
export const mapDataToSocialProfile = (friendshipNeeds, answers) => {
  if (!friendshipNeeds || !answers) return null;
  
  // Map communication style to desired weekly interactions
  const communicationAnswer = answers['communication-style'];
  const desiredWeeklyMap = {
    'frequent': 7, // daily
    'regular': 3,  // weekly
    'occasional': 1 // monthly
  };
  const desired_weekly_interactions = desiredWeeklyMap[communicationAnswer?.value] || 3;
  
  // Map connection depth to preferred interaction types
  const depthAnswer = answers['connection-depth'];
  const preferred_interaction_types = depthAnswer?.value === 'deep' 
    ? ['deep_1on1'] 
    : depthAnswer?.value === 'balanced'
    ? ['deep_1on1', 'casual_checkin']
    : ['casual_checkin', 'group'];
  
  // Map social energy
  const energyAnswer = answers['social-energy'];
  const social_energy_level = energyAnswer?.value === 'high' 
    ? 'extrovert'
    : energyAnswer?.value === 'low'
    ? 'introvert'
    : 'ambivert';
  
  // Map friendship goals
  const goalsAnswer = answers['friendship-goals'];
  const friendship_goal = goalsAnswer?.value === 'growth' || goalsAnswer?.value === 'support'
    ? 'deepen_existing'
    : goalsAnswer?.value === 'fun'
    ? 'expand_network'
    : 'deepen_existing'; // default
  
  // Estimate loneliness score from gap between desired and actual (simplified)
  // This would ideally come from a direct question, but we'll estimate
  const loneliness_score = 5; // Default middle, could be enhanced with actual assessment
  
  // Current energy - default to medium, could be user input
  const current_energy_today = 'medium';
  
  return {
    desired_weekly_interactions,
    preferred_interaction_types,
    social_energy_level,
    friendship_goal,
    loneliness_score,
    current_energy_today
  };
};

/**
 * Map friends and contact history to friendship tracker format
 */
export const mapDataToFriendshipTracker = (friends, contactHistory) => {
  if (!friends || !Array.isArray(friends)) return [];
  
  return friends.map(friend => {
    // Get contact history for this friend
    const friendContacts = (contactHistory || []).filter(contact => {
      // Match by friendId, friendName, or friend object
      if (contact.friendId === friend.id) return true;
      if (contact.friendName === friend.name) return true;
      if (contact.friend && (contact.friend.id === friend.id || contact.friend.name === friend.name)) return true;
      return false;
    });
    
    // Calculate usual contact gap
    const gaps = [];
    friendContacts.sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
    for (let i = 0; i < friendContacts.length - 1; i++) {
      const date1 = new Date(friendContacts[i].date || friendContacts[i].createdAt);
      const date2 = new Date(friendContacts[i + 1].date || friendContacts[i + 1].createdAt);
      const gap = Math.floor((date1 - date2) / (1000 * 60 * 60 * 24));
      gaps.push(gap);
    }
    const usual_contact_gap_days = gaps.length > 0 
      ? Math.round(gaps.reduce((a, b) => a + b, 0) / gaps.length)
      : friend.contactFrequency === 'daily' ? 1
      : friend.contactFrequency === 'weekly' ? 7
      : friend.contactFrequency === 'biweekly' ? 14
      : friend.contactFrequency === 'monthly' ? 30
      : 14; // default
    
    // Calculate average interaction quality (simplified - would need quality data)
    const avg_interaction_quality = friendContacts.length > 0 ? 4 : 3; // Default to good
    
    // Map recent interactions
    const recent_interactions = friendContacts.slice(0, 5).map(contact => {
      // Handle Firestore Timestamp
      let contactDate = contact.date || contact.createdAt;
      if (contactDate && contactDate.toDate) {
        contactDate = contactDate.toDate().toISOString();
      } else if (contactDate && typeof contactDate === 'object') {
        contactDate = new Date(contactDate).toISOString();
      }
      
      return {
        date: contactDate,
        type: contact.type || 'other',
        quality: contact.quality || contact.avgQuality || 4, // Default, would come from reflection data
        initiated_by: contact.initiatedBy || contact.initiated_by || 'me'
      };
    });
    
    // Calculate reciprocity score (simplified)
    const initiatedByMe = recent_interactions.filter(i => i.initiated_by === 'me').length;
    const reciprocity_score = recent_interactions.length > 0 
      ? 1 - (initiatedByMe / recent_interactions.length)
      : 0.5; // Default
    
    // Handle last contact date (could be Firestore Timestamp or string)
    let lastContactDate = friend.lastContactDate || friend.last_contact_date;
    if (!lastContactDate && friendContacts.length > 0) {
      lastContactDate = friendContacts[0].date || friendContacts[0].createdAt;
    }
    if (lastContactDate && lastContactDate.toDate) {
      lastContactDate = lastContactDate.toDate().toISOString();
    } else if (lastContactDate && typeof lastContactDate === 'object') {
      lastContactDate = new Date(lastContactDate).toISOString();
    }
    
    return {
      name: friend.name,
      relationship_type: friend.relationship || friend.relationship_type || 'friend',
      last_contact_date: lastContactDate,
      usual_contact_gap_days,
      avg_interaction_quality,
      recent_interactions,
      reciprocity_score,
      countsAsCloseFriend: friend.countsAsCloseFriend || false // Allow marking any friend as "close" for goal tracking
    };
  });
};
